import React, { useEffect, useState } from 'react';
import {
    Box,
    Button, CircularProgress, ClickAwayListener,
    Menu,
    MenuItem,
    Table,
    TableBody,
    TableCell, TableFooter,
    TableHead, TablePagination, TableProps,
    TableRow,
    TableRowProps, Typography
} from '@mui/material';
import styled from 'styled-components';
import { toast } from 'react-toastify';

import ItemListDialog, { ItemListDataItem } from './item-list-dialog';
import DataGrid from '../../components/grid/dataGrid';
import GlobalInput from '../../components/global/input';
import GlobalButton from '../../components/global/button';
import FilterContainer from '../../components/layout/filterContainer';
import { CustomTableCheckboxCell } from '../../components/grid/CustomTableCell';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus-icon.svg';
import { ReactComponent as RefreshIcon } from '../../assets/icons/refresh-icon.svg';
import { ReactComponent as FilterIcon } from '../../assets/icons/filter-icon.svg';

import { useAppSelector } from '../../hooks/hooks';
import EventApi from '../../api/event.api';
import MEventApi from "../../api/mevent.api";
import {filter, first} from "rxjs";
import GlobalDropdown from "../../components/global/dropdown";
import CompanyDialog, {CompanyDataItem} from "./company-dialog";
import {colors} from "../../theme";
import {ReactComponent as MapIcon} from "../../assets/icons/map-icon.svg";
import MenuDropdown, {ContainerProps, OptionProps} from "../../components/layout/menuDropdown";
import {ReactComponent as ArrowIcon} from "../../assets/icons/menu-arrow-icon.svg";
import {ReactComponent as EditIcon} from "../../assets/icons/edit-icon.svg";
import ClientDialog, {ClientDataItem} from "./client-dialog";
import moment from "moment";
import ContentContainer from "../../components/layout/contentContainer";

const Contact = () => {

    const eventApi = new MEventApi();
    
    const defaultFilterObject = {
        company: null,
        firstName: '',
        lastName: ''
    }
    
    const addNewList = [
        {
            name: 'Company'
        },
        {
            name: 'Contact'
        }
    ]

    const [state, setState] = useState({
        companyDialog: {
            isOpen: false,
            data: null,
        },
        clientDialog: {
            isOpen: false,
            data: null,
        },
        companies: [],
        filterObject: {...defaultFilterObject},
        clients: [],
        showLoading: false,
    });

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    
    useEffect(() => {

        const fetchData = async () => {
            setState(prevState => ({ ...prevState, showLoading: true }));
            try {
                const companies = await eventApi.GetCompanies();
                const clients = await eventApi.GetClients();
                setState(prevState => ({
                    ...prevState,
                    companies: companies,
                    clients: clients,
                }));
            } finally {
                setState(prevState => ({ ...prevState, showLoading: false }));
            }
        };

        fetchData();
    }, []);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleFilterObjChange = (name: any, value: any) => {
        if (value === '') value = null;

        setState(prevState => ({
            ...prevState,
            filterObject: {
                ...state.filterObject,
                [name]: value,
            },
        }));

        setPage(0);
    };

    const handleClearAllFilters = () => {
        setState(prevState => ({
            ...prevState,
            filterObject: { ...defaultFilterObject },
        }));
        setPage(0);
    };
    
    const handleAddNew = (event: any) => {
        const {
            target: { value },
        } = event;
        
        if (value.name === 'Company') {
            handleAddNewCompany()
        } else if (value.name === 'Contact') {
            handleAddNewClient()
        }
    }

    const handleAddNewCompany = () => {
        const data = {
            id: null,
            name: ""
        }

        handleCompanyDialog(data);
    };

    const handleAddNewClient = () => {
        const data = {
            id: null,
            company: {
                id: null,
                name: ""
            },
            subCompanyName: "",
            firstName: "",
            lastName: "",
            address: "",
            contactEmail: "",
            contactPhone: "",
            comment: "",
            lastActivityDate: null,
            isActive: true,
        }

        handleClientDialog(data);
    };

    const handleCompanyDialog = (data: any = null) => {
        setState(prevState => ({
            ...prevState,
            companyDialog: {
                isOpen: !prevState.companyDialog.isOpen,
                data: data,
            },
        }));
    }

    const handleClientDialog = (data: any = null) => {
        setState(prevState => ({
            ...prevState,
            clientDialog: {
                isOpen: !prevState.clientDialog.isOpen,
                data: data,
            },
        }));
    }

    const addCompany = async (company: CompanyDataItem) => {
        setState(prevState => ({ ...prevState, showLoading: true }));
        try {
            await eventApi.PostCompanies(company);
            const companies = await eventApi.GetCompanies();
            handleCompanyDialog();
            setState(prevState => ({
                ...prevState,
                companies: companies,
            }))
        } catch (error) {
            toast.error('Error adding company');
        } finally {
            setState(prevState => ({ ...prevState, showLoading: false }));
        }
    }
    
    const addClient = async (client: ClientDataItem) => {
        setState(prevState => ({ ...prevState, showLoading: true }));
        try {
            await eventApi.PostClient(client);
            const clients = await eventApi.GetClients();
            handleClientDialog();
            setState(prevState => ({
                ...prevState,
                clients: clients,
            }))
        } catch (error) {
            toast.error('Error adding client');
        } finally {
            setState(prevState => ({ ...prevState, showLoading: false }));
        }
    }

    const saveClient = async (client: ClientDataItem) => {
        setState(prevState => ({ ...prevState, showLoading: true }));
        try {
            await eventApi.PutClient(client);
            const clients = await eventApi.GetClients();
            handleClientDialog();
            setState(prevState => ({
                ...prevState,
                clients: clients,
            }))
        } catch (error) {
            toast.error('Error saving client');
        } finally {
            setState(prevState => ({ ...prevState, showLoading: false }));
        }
    }

    const getGridData = () => {
        const { company, firstName, lastName } = state.filterObject;

        let filteredClients = [...state.clients];
        let filteredCompanies = [...state.companies];

        if (company) {
            filteredCompanies = filteredCompanies.filter(
                (d) => d.name.toLowerCase() === company.name.toLowerCase()
            );
        }

        if (firstName) {
            filteredClients = filteredClients.filter((d) =>
                d.firstName.toLowerCase().includes(firstName.toLowerCase())
            );
        }

        if (lastName) {
            filteredClients = filteredClients.filter((d) =>
                d.lastName.toLowerCase().includes(lastName.toLowerCase())
            );
        }

        const clientCompanyIds = new Set(filteredClients.map((c) => c.company.id));
        filteredCompanies = filteredCompanies.filter((c) => clientCompanyIds.has(c.id));

        return [filteredClients, filteredCompanies];
    };


    const getPaginatedCompanies = () => {
        const companies = getGridData()[1];
        return companies.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    }

    return <>
        <FilterContainer sx={{ flexDirection: 'column', p: '7px' }}>

            <FlexBox sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <FlexBox>
                    <GlobalDropdown label={'Company'}
                                    field={'id'}
                                    dataItemKey={'company'}
                                    data={state.companies}
                                    name={'name'}
                                    value={state.filterObject?.company}
                                    onChange={handleFilterObjChange}
                                    placeholder={'Select Company'}
                                    isSearch
                                    disabled={state.showLoading}
                                    sx={{ minWidth: '400px' }}
                    />
                    <GlobalInput label="filter" icon={<FilterIcon />} placeholder="First Name" name={'firstName'} value={state.filterObject?.firstName} onChange={handleFilterObjChange} disabled={state.showLoading} />
                    <GlobalInput placeholder="Last Name" name={'lastName'} value={state.filterObject?.lastName} onChange={handleFilterObjChange} disabled={state.showLoading} />
                    <GlobalButton variant="round" text="clear all" secondary={'true'} endIcon={<RefreshIcon />} onClick={handleClearAllFilters} disabled={state.showLoading} />

                </FlexBox>
                <FlexBox sx={{
                    width: '10%',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    height: '100%',
                }}>
                    <MenuDropdown 
                        icon={<PlusIcon />} 
                        isHover 
                        placeholder={'Add'} 
                        data={addNewList} 
                        name={'name'}
                        dropdownBackgroundColor={colors.blue.main}
                        dropdownBackgroundHoverColor={colors.blue.dark}
                        optionsBackgroundColor={colors.white}
                        optionsBackgroundHoverColor={colors.navy.light}
                        optionsColor={colors.blue.main}
                        onChange={handleAddNew}
                        disabled={state.showLoading}
                        borderRadius={'0px'}
                    />
                </FlexBox>
            </FlexBox>
        </FilterContainer>

        {state.showLoading ? (
            <ContentContainer>
                <FlexBox sx={{ alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <CircularProgress size={100} />
                </FlexBox>
            </ContentContainer>
        ) : (
            <>
                {state.companyDialog.isOpen && (
                    <CompanyDialog
                        data={state.companyDialog?.data}
                        companies={state.companies}
                        onClose={handleCompanyDialog}
                        onCreate={addCompany}
                    />
                )}

                {state.clientDialog.isOpen && (
                    <ClientDialog
                        data={state.clientDialog?.data}
                        companies={state.companies}
                        onClose={handleClientDialog}
                        onSave={saveClient}
                        onCreate={addClient}
                    />
                )}

                <StyledTable size="small" sx={{ marginTop: '10px' }}>
                    <StyledTableHeaders>
                        <TableCell sx={{ fontSize: '12px', fontWeight: '500', lineHeight: '1.5em', userSelect: 'none', userDrag: 'none' }}>Sub Company</TableCell>
                        <TableCell sx={{ fontSize: '12px', fontWeight: '500', lineHeight: '1.5em', userSelect: 'none', userDrag: 'none' }}>First Name</TableCell>
                        <TableCell sx={{ fontSize: '12px', fontWeight: '500', lineHeight: '1.5em', userSelect: 'none', userDrag: 'none' }}>Last Name</TableCell>
                        <TableCell sx={{ fontSize: '12px', fontWeight: '500', lineHeight: '1.5em', userSelect: 'none', userDrag: 'none' }}>Contact Email</TableCell>
                        <TableCell sx={{ fontSize: '12px', fontWeight: '500', lineHeight: '1.5em', userSelect: 'none', userDrag: 'none' }}>Contact Phone</TableCell>
                        <TableCell sx={{ fontSize: '12px', fontWeight: '500', lineHeight: '1.5em', userSelect: 'none', userDrag: 'none' }}>Last Activity</TableCell>
                        <TableCell sx={{ fontSize: '12px', fontWeight: '500', lineHeight: '1.5em', userSelect: 'none', userDrag: 'none' }}>Active</TableCell>
                        <TableCell sx={{ fontSize: '12px', fontWeight: '500', lineHeight: '1.5em', userSelect: 'none', userDrag: 'none' }}></TableCell>
                    </StyledTableHeaders>
                    <TableBody>
                        {getPaginatedCompanies().map((entry, i) => {
                            const filteredClientsGridData = getGridData()[0].filter((e) => e.company.id === entry.id);

                            return (
                                <>
                                    {(filteredClientsGridData.length > 0 ||
                                        !(state.filterObject.firstName || state.filterObject.lastName)) && (
                                        <StyledTableRowForCompany>
                                            <TableCell style={{ fontWeight: 'bold', color: colors.blue.main }} colSpan={8}>{entry.name}</TableCell>
                                        </StyledTableRowForCompany>
                                    )}
                                    {filteredClientsGridData.map((filteredClient, j) => {
                                        return (
                                            <>
                                                <StyledTableRow>
                                                    <TableCell>{filteredClient.subCompanyName}</TableCell>
                                                    <TableCell>{filteredClient.firstName}</TableCell>
                                                    <TableCell>{filteredClient.lastName}</TableCell>
                                                    <TableCell>{filteredClient.contactEmail}</TableCell>
                                                    <TableCell>{filteredClient.contactPhone}</TableCell>
                                                    <TableCell>{filteredClient.lastActivityDate ? moment(filteredClient.lastActivityDate).format("YYYY-MM-DD") : null}</TableCell>
                                                    <CustomTableCheckboxCell
                                                        data={filteredClient}
                                                        name={'isActive'}
                                                    />
                                                    <TableCell>
                                                        <EditIcon
                                                            id="edit"
                                                            style={{ opacity: '0.5', pointerEvents: 'all', cursor: 'pointer' }}
                                                            onClick={() => {
                                                                handleClientDialog(filteredClient)
                                                            }}
                                                        />
                                                    </TableCell>
                                                </StyledTableRow>
                                            </>
                                        )
                                    })}
                                </>
                            )
                        })}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25, 50]}
                                count={getGridData()[1].length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        </TableRow>
                    </TableFooter>
                </StyledTable>
            </>
            
        )}
    </>;
};

export default Contact;

const FlexBox = styled(Box)`
    && {
        display: flex;

        & > * {
            margin-right: 10px;
        }

        & > :last-child {
            margin-right: 0px;
        }
    }
`;

const StyledTableHeaders = styled(TableRow) <TableRowProps>`
    && {
        td {
            border-bottom: 1px solid ${colors.grey.light} !important;
        }
    }
`;

const StyledTableRow = styled(TableRow) <TableRowProps>`
    && {
        background-color: ${colors.white};

        &:hover {
            background-color: ${colors.blue.light};
        }

        td {
            padding: ${'10px'};
            border: none !important;
        }
    }
`;

const StyledTableRowForCompany = styled(TableRow) <TableRowProps>`
    && {
        background-color: ${colors.grey.light};

        td {
            padding: ${'10px'};
            border: none !important;
        }
    }
`;

const StyledTable = styled(Table) <TableProps>`
    && {
        background-color: ${colors.white};
        padding: 2px 12px;
        overflow-y: hidden;
    }
`;